import _ from 'lodash';
import { MoveStates, PostStatus, UNORGANIZED_TOPIC_ID } from '../constants';
import isTopicPostPinned from './isTopicPostPinned';

type PinOperation =
  | {
      operationName: 'PIN_POST';
      postId: string;
      topicId: string;
      pinnedOrder: number;
    }
  | {
      operationName: 'UNPIN_POST';
      postId: string;
      topicId: string;
    };

type TopicPostOperation = {
  operationName: 'REMOVE_TOPIC_FROM_POST' | 'ADD_TOPIC_TO_POST';
  postId: string;
  topicId: string;
};

type PostStatusOperation =
  | {
      operationName: 'DELETE_POST';
      id: string;
    }
  | {
      operationName: 'UPDATE_POST';
      id: string;
      published: boolean;
    }
  | {
      operationName: 'UPDATE_POST';
      id: string;
      archived: boolean;
    };

type PostOperation = TopicPostOperation | PinOperation | PostStatusOperation;

export type TopicPost = {
  node: {
    id: string;
  };
  pinnedOrder: number | null;
  topicId?: string;
  reorganizationMetadata: {
    move?: {
      state: MoveStates;
    };
    status: PostStatus;
    isPinned: boolean | null;
  };
};

function getPostStatusOperations(
  status: PostStatus,
  postId: string,
): PostStatusOperation | null {
  switch (status) {
    case PostStatus.deleted:
      return {
        operationName: 'DELETE_POST',
        id: postId,
      };
    case PostStatus.published:
    case PostStatus.unpublished:
      return {
        operationName: 'UPDATE_POST',
        id: postId,
        published: status === PostStatus.published,
      };
    case PostStatus.archived:
    case PostStatus.unarchived:
      return {
        operationName: 'UPDATE_POST',
        id: postId,
        archived: status === PostStatus.archived,
      };
    default:
      return null;
  }
}

interface GetPostsOperationsArgs {
  parentId: string;
  topicPosts: TopicPost[];
}

const getPostsOperations = ({
  parentId,
  topicPosts,
}: GetPostsOperationsArgs): PostOperation[] => {
  const moveAndPinOperations = topicPosts.flatMap((topicPost, index) => {
    const operations: PostOperation[] = [];

    const {
      node: post,
      reorganizationMetadata: { move, isPinned },
    } = topicPost;

    const state = move?.state;

    if (state === MoveStates.movedOut && parentId !== UNORGANIZED_TOPIC_ID) {
      operations.push({
        operationName: 'REMOVE_TOPIC_FROM_POST',
        postId: post.id,
        topicId: parentId,
      });
    }

    if (state === MoveStates.movedIn) {
      operations.push({
        operationName: 'ADD_TOPIC_TO_POST',
        postId: post.id,
        topicId: parentId,
      });
    }

    if (state === MoveStates.initial && isPinned === false) {
      operations.push({
        operationName: 'UNPIN_POST',
        postId: post.id,
        topicId: parentId,
      });
    }

    if (
      isTopicPostPinned(topicPost) &&
      (state === MoveStates.movedIn || index !== topicPost.pinnedOrder)
    ) {
      operations.push({
        operationName: 'PIN_POST',
        postId: post.id,
        topicId: parentId,
        pinnedOrder: index,
      });
    }

    return operations;
  });

  const postStatusOperations = _.compact(
    topicPosts.map((topicPost) => {
      return getPostStatusOperations(
        topicPost.reorganizationMetadata.status,
        topicPost.node.id,
      );
    }),
  );

  return [...moveAndPinOperations, ...postStatusOperations];
};

export default getPostsOperations;
